/**
 * Lane C0-FACE (lab only). The proposed face as a rig layer: `labFaceLayer` stands in for figure.ts `faceLayer`
 * (same id 'face', same depth) so `compose` treats it exactly as the live face: skin added to the jaw at art
 * resolution, the features at full resolution (`fine`): the traced C0 eyes and brows with more variants and
 * expression steps (fine.ts), coloured the way figure.ts colours the live ones, plus nose, mouth, cues and marks
 * (grids.ts); facial hair and earrings from the live face parts. Research: docs/research/C0_FACES.md.
 */
import type { Pose } from '../../sprites';
import { shade } from '../../palette';
import { DEFAULT_FACE, type FaceLook, faceStamps } from '../face';
import { type Layer, px } from '../figure';
import type { Skeleton } from '../skeleton';
import { type Age, byId, type Expr, JAWS, type Sex } from './parts';
import { cueStamps, featureStamps, mouthPlaced, mouthRows, type Placed } from './grids';
import { fineFace } from '../face2';
import { fineFeatures } from './fine';
import { type CrispPx, crispPixels, roleColours } from './crisp';

/** One face of the proposal: a part per slot, the age cues, colours, and the expression (or `auto`: from the pose). */
export interface LabFace {
  sex: Sex;
  age: Age;
  eyes: string;
  brows: string;
  nose: string;
  mouth: string;
  jaw: string;
  /** Cheeks flushed (the young, women). */
  blush?: boolean;
  /** The look's own blush colour (the old style draws the game's blush mark in it). */
  blushColour?: string;
  /** Cheek and smile lines for a weathered adult (age cues come with `elder` anyway). */
  lines?: boolean;
  iris: string;
  /** Brow and facial-hair colour; defaults to the hair's (as the live face). */
  brow?: string;
  /** Lip colour for women. */
  lip?: string;
  /** Scars, moles, wrinkles (parts.ts MARKS). */
  marks?: readonly string[];
  /** Facial hair as the live face knows it (face.ts parts). */
  beard?: string;
  mustache?: string;
  sideburns?: string;
  expr: Expr | 'auto';
  /**
   * `old` (young and adult people, user 2026-10-10): the game's current face — the live part ids (face2.ts) for
   * eyes, brows, nose and mouth, no jaw or age cues — sharpened by crisp.ts. `new` (elders): the proposal's set.
   */
  style?: 'old' | 'new';
}

/** The expression a pose shows when the face is on `auto`. */
export function exprOf(sk: Skeleton): Expr {
  if (sk.rig.closed) return 'blink';
  const hurt: readonly Pose[] = ['hurt', 'blockHit', 'fall'];
  if (hurt.includes(sk.pose)) return 'hurt';
  if (sk.rig.face === 'atk') return 'shout';
  if (sk.rig.face === 'block' || sk.rig.face === 'cast') return 'angry';
  return 'neutral';
}

// ── colour ──

const hex = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16));
function mix(a: string, b: string, k: number): string {
  const A = hex(a);
  const B = hex(b);
  return `#${A.map((v, i) => Math.round(v + (B[i]! - v) * k).toString(16).padStart(2, '0')).join('')}`;
}

/** Colours for the fine features. */
function palette(f: LabFace, skin: { skin: string; skinS: string }, hair: string): Record<string, string> {
  const lashBase = '#1c1214';
  const k = mix(lashBase, shade(hair, -0.3), 0.15);
  const browC = f.brow ?? (f.age === 'elder' ? mix(hair, skin.skinS, 0.25) : mix(shade(hair, -0.12), lashBase, 0.35));
  const line = mix(skin.skinS, '#5a2420', 0.45);
  return {
    k,
    l: mix(k, skin.skinS, 0.45),
    c: mix(skin.skin, skin.skinS, 0.65),
    w: '#f6f1ea',
    v: mix('#f6f1ea', skin.skinS, 0.4),
    d: shade(f.iris, -0.12),
    i: f.iris,
    h: shade(f.iris, 0.14),
    P: mix(shade(f.iris, -0.25), '#120a0a', 0.6),
    I: '#ffffff',
    b: browC,
    B: f.age === 'elder' ? mix(browC, skin.skinS, 0.3) : shade(browC, -0.06),
    s: mix(skin.skin, skin.skinS, 0.75),
    S: shade(skin.skinS, -0.06),
    H: shade(skin.skin, 0.06),
    m: line,
    n: mix(skin.skin, line, 0.5),
    L: f.lip ?? '#c4626a',
    M: '#4a1a1e',
    t: '#f4eee6',
    B2: '#e89a92',
    x: mix(skin.skin, '#f4d8cc', 0.55),
    X: mix(skin.skinS, '#9a5a50', 0.35),
    o: '#3a2420',
  };
}

const hairColours = (fh: string) => ({
  h: fh,
  H: shade(fh, -0.1),
  g: '#e8c27e',
  G: '#c9ced8',
  j: '#5fbf9a',
  W: '#ece6da',
  k: '#1a1010',
});

/**
 * The proposed face as a layer (id 'face', like figure.ts `faceLayer`, so `compose` lets its fine features stand
 * on their own the same way). `expr` 'auto' reads the pose (exprOf).
 */
export function labFaceLayer(f: LabFace, skin: { skin: string; skinS: string }, hair: string, facialHair?: string): Layer {
  const pal = palette(f, skin, hair);
  const roles = roleColours({ ...skin, iris: f.iris, sclera: '#f6f1ea', brow: f.brow ?? hair });
  const fh = facialHair ?? hair;
  // Facial hair, sideburns and earrings from the live parts (face.ts), at art resolution over the hair.
  const live: FaceLook = {
    ...DEFAULT_FACE,
    beard: f.beard ?? 'none',
    mustache: f.mustache ?? 'none',
    sideburns: f.sideburns ?? 'none',
    // The old style keeps the game's blush mark (art resolution, as today).
    marks: f.style === 'old' && f.blush ? ['blush'] : [],
  };
  const hc = hairColours(fh);
  const jaw = byId(JAWS, f.jaw);
  return {
    id: 'face',
    label: 'ใบหน้า (ทดลอง)',
    pieces(sk) {
      const h = sk.joints.head;
      const x = Math.round(h.x);
      const y = Math.round(h.y);
      const expr = f.expr === 'auto' ? exprOf(sk) : f.expr;
      const view = sk.view;
      const { inner: innerStamps, hair: hairStamps } = faceStamps(live, view, 'idle');
      const add = jaw.add[view] ?? [];
      const cells: [number, number][] =
        view === 'front' ? add.flatMap(([c, r]) => [[c, r] as [number, number], [17 - c, r] as [number, number]]) : [...add];
      return [
        {
          z: h.z + 0.01,
          draw: (g) => {
            // HEAD grid origin: column 9, row 9 on the joint (heads.ts).
            for (const [c, r] of cells) px(g, x - 9 + c, y - 9 + r, 1, 1, skin.skin);
            for (const st of innerStamps)
              st.rows.forEach((row, r) => {
                for (let c = 0; c < row.length; c++)
                  if (row[c] === 'B') px(g, x + st.x + c, y + st.y + r, 1, 1, f.blushColour ?? '#eaa098');
              });
          },
          fine: (g, res) => {
            if (res !== 2) return;
            const X = x * 2;
            const Y = y * 2;
            const put = (list: Placed[], map: (ch: string) => string | undefined) => {
              for (const st of list)
                st.rows.forEach((row, r) => {
                  for (let c = 0; c < row.length; c++) {
                    const col = map(row[c]!);
                    if (!col) continue;
                    g.fillStyle = col;
                    g.fillRect(X + st.x + c, Y + st.y + r, 1, 1);
                  }
                });
            };
            put(cueStamps(f, view, expr, !!sk.left), (ch) =>
              ch === 'B' ? mix(pal.B2!, skin.skin, 0.35) : ch === 'c' ? pal.c : ch === 'x' ? pal.x : ch === 'X' ? pal.X : ch === 'o' ? pal.o : undefined,
            );
            if (f.style === 'old') {
              // The traced nose and resting mouth, sharpened; an expression opens a mouth of the traced one's size.
              const tf = view === 'front' || view === 'front3' ? fineFace(view, { eyes: 'ref', brows: 'ref', nose: f.nose, mouth: f.mouth }) : undefined;
              if (tf?.nose) for (const p of crispPixels(tf.nose, 'nose', view)) { g.fillStyle = roles[p.role]; g.fillRect(X + p.x, Y + p.y, 1, 1); }
              const covered = (f.beard === 'long' || f.mustache === 'walrus') && !['shout', 'talk', 'hurt'].includes(expr);
              if (!covered) {
                if (expr === 'neutral' || expr === 'blink') {
                  if (tf?.mouth) for (const p of crispPixels(tf.mouth, 'mouth', view)) { g.fillStyle = roles[p.role]; g.fillRect(X + p.x, Y + p.y, 1, 1); }
                } else {
                  const mp = mouthPlaced(view, mouthRows({ id: 'old', label: '', rows: ['nmn'], lips: f.sex === 'female' }, expr, f.sex));
                  if (mp) put([mp], (ch) => (ch === '.' ? undefined : pal[ch]));
                }
              }
            } else put(featureStamps(f, view, expr, !!sk.left), (ch) => (ch === '.' ? undefined : pal[ch]));
            if (view === 'front' || view === 'front3' || view === 'side') {
              // The traced eyes and brows with the proposal's variants and expression (fine.ts), sharpened (crisp.ts).
              const ff = fineFeatures(view, f.eyes, f.brows, expr);
              const paint = (px: CrispPx[]) => {
                for (const p of px) {
                  g.fillStyle = roles[p.role];
                  g.fillRect(X + p.x, Y + p.y, 1, 1);
                }
              };
              const eyes = expr === 'blink' ? ff?.closed : ff?.eyes;
              if (eyes) paint(crispPixels(eyes, expr === 'blink' ? 'closed' : 'eyes', view));
              if (ff?.brows) paint(crispPixels(ff.brows, 'brows', view));
            }
          },
        },
        {
          z: h.z + 0.025,
          draw: (g) => {
            for (const st of hairStamps)
              st.rows.forEach((row, r) => {
                for (let c = 0; c < row.length; c++) {
                  const col = hc[row[c] as keyof typeof hc];
                  if (col) px(g, x + st.x + c, y + st.y + r, 1, 1, col);
                }
              });
          },
        },
      ];
    },
  };
}
